import React, { useEffect, useRef } from 'react';
import { Animated, TouchableOpacity, View, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  visible: boolean;
  onDismiss: () => void;
  duration?: number;
}

const TOAST_CONFIG: Record<ToastType, { bg: string; icon: keyof typeof MaterialIcons.glyphMap; iconColor: string }> = {
  success: { bg: 'bg-green-600', icon: 'check-circle', iconColor: '#ffffff' },
  error: { bg: 'bg-red-600', icon: 'error', iconColor: '#ffffff' },
  info: { bg: 'bg-blue-600', icon: 'info', iconColor: '#ffffff' },
};

export function Toast({ message, type, visible, onDismiss, duration = 4000 }: ToastProps) {
  const insets = useSafeAreaInsets();
  const translateY = useRef(new Animated.Value(-100)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.spring(translateY, { toValue: 0, useNativeDriver: true, tension: 80, friction: 12 }),
        Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }),
      ]).start();

      timerRef.current = setTimeout(() => {
        dismiss();
      }, duration);
    } else {
      dismiss();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visible]);

  const dismiss = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    Animated.parallel([
      Animated.timing(translateY, { toValue: -100, duration: 250, useNativeDriver: true }),
      Animated.timing(opacity, { toValue: 0, duration: 200, useNativeDriver: true }),
    ]).start(() => {
      onDismiss();
    });
  };

  if (!visible && !message) return null;

  const config = TOAST_CONFIG[type];
  const topOffset = Platform.OS === 'ios' ? insets.top + 8 : insets.top + 16;

  return (
    <Animated.View
      style={{
        position: 'absolute',
        top: topOffset,
        left: 16,
        right: 16,
        zIndex: 9999,
        transform: [{ translateY }],
        opacity,
      }}
    >
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={dismiss}
        className={`${config.bg} rounded-xl px-4 py-3 flex-row items-center shadow-lg`}
        style={{ shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 8, elevation: 8 }}
      >
        <MaterialIcons name={config.icon} size={22} color={config.iconColor} />
        <Text className="text-white text-sm font-medium ml-3 flex-1">{message}</Text>
        <MaterialIcons name="close" size={18} color="rgba(255,255,255,0.7)" />
      </TouchableOpacity>
    </Animated.View>
  );
}

export default Toast;
